import { Box, Grid2, Tab, Tabs } from "@mui/material";
import { useState } from "react";

import type { MillId } from "@/setup/mill-config/mill-config";
import type { Section } from "../../../models/Section";
import { SectionFlowSheet } from "./SectionFlowSheet";
import { SectionSpecEditor } from "./section-spec/SectionSpecEditor";
import { SectionStandsGrid } from "./mill-config/SectionStandsGrid";

const SECTION_TAB = {
	STAND_SETUP: "stand-setup",
	FLOW_SHEET: "flow-sheet",
	SPECIFICATIONS: "specifications",
} as const;

type SectionTab = (typeof SECTION_TAB)[keyof typeof SECTION_TAB];

interface SectionReferenceTabsProps {
	section: Section;
	sectionId: number;
	assetId: MillId;
	disabled: boolean;
	showFlowSheet: boolean;
	showSpecifications: boolean;
	onError: (error: Error) => void;
}

export const SectionReferenceTabs = ({
	section,
	sectionId,
	assetId,
	disabled,
	showFlowSheet,
	showSpecifications,
	onError,
}: SectionReferenceTabsProps) => {
	const [selectedTab, setSelectedTab] = useState<SectionTab>(
		SECTION_TAB.STAND_SETUP,
	);
	const isTabAvailable = {
		[SECTION_TAB.STAND_SETUP]: true,
		[SECTION_TAB.FLOW_SHEET]: showFlowSheet,
		[SECTION_TAB.SPECIFICATIONS]: showSpecifications,
	};
	const activeTab = isTabAvailable[selectedTab]
		? selectedTab
		: SECTION_TAB.STAND_SETUP;

	return (
		<Box
			component="section"
			sx={{ borderTop: 1, borderColor: "divider", mt: 1, pt: 1 }}
		>
			<Tabs
				value={activeTab}
				onChange={(_, tab: SectionTab) => setSelectedTab(tab)}
				aria-label="Section details"
				sx={{ borderBottom: 1, borderColor: "divider", mb: 1.5 }}
			>
				<Tab
					id="section-tab-stand-setup"
					value={SECTION_TAB.STAND_SETUP}
					label="Stand setup"
				/>
				{showFlowSheet && (
					<Tab
						id="section-tab-flow-sheet"
						value={SECTION_TAB.FLOW_SHEET}
						label="Flow Sheet"
					/>
				)}
				{showSpecifications && (
					<Tab
						data-test-id="section-specifications-tab"
						id="section-tab-specifications"
						value={SECTION_TAB.SPECIFICATIONS}
						label="Specifications"
					/>
				)}
			</Tabs>

			{activeTab === SECTION_TAB.STAND_SETUP ? (
				<Box role="tabpanel" id="section-panel-stand-setup">
					<Grid2 container>
						<SectionStandsGrid
							section={section}
							assetId={assetId}
							disabled={disabled}
						/>
					</Grid2>
				</Box>
			) : activeTab === SECTION_TAB.FLOW_SHEET ? (
				<Box role="tabpanel" id="section-panel-flow-sheet">
					<SectionFlowSheet
						key={sectionId}
						sectionId={sectionId}
						sectionBilletSize={section.BilletSize}
						onError={onError}
					/>
				</Box>
			) : (
				<Box
					role="tabpanel"
					data-test-id="section-specifications-panel"
					id="section-panel-specifications"
					aria-labelledby="section-tab-specifications"
				>
					<SectionSpecEditor
						key={`${sectionId}:${assetId}`}
						sectionId={sectionId}
						assetId={assetId}
						sectionName={section.SectionName}
						onError={onError}
					/>
				</Box>
			)}
		</Box>
	);
};
